/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { AreaOfExpertiseDetails } from '../../../models/ProviderDetails';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { getCurrentMember } from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import {
  Constants,
  FacilityCategory,
  ReverseCoverageTypesCodes,
} from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import {
  PlatinumBadgeStyle,
  landingDrawerIcon,
  mobileOnly,
  phoneOnly,
} from '../../ConstantsStyles';
import { LinkContainer } from '../../LinkContainer/LinkContainer';
import { CulturalCompetencyTraining } from '../../modal/CulturalCompetencyTraining';
import { AoeDrawer } from '../../ProviderDetailTabs/About/CollapseProviderDetailsSection/AoeDrawer';
import { ListOtherProviderDetails } from '../../ProviderDetailTabs/About/CollapseProviderDetailsSection/ListOtherProviderDetails';
import {
  StackDiv,
  headerText,
  qualityText,
  stackDivStyle,
} from '../../ProviderDetailTabs/Quality/Quality.styled';
import { getFeatureFlag } from '../../Utils';
import { convertProviderTypeToAdobeType } from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { isCnsMember } from '../../Utils/memberUtils/memberUtils';
import { hasKeyCARF } from '../Utils';
import {
  LeapFrogContainer,
  TabContainer,
  leapFrogTextStyle,
} from './FacilityQuality.styled';
import { LeapFrogRating } from './LeapFrogRating/LeapFrogRating';

type Props = {
  providerName: string;
  providerType: string;
  selectedLocation: FacilityLocationsResponse;
  tabTitle?: string;
  organizationCode?: string[];
  areaOfExpertise?: string[] | null;
  areaOfExpertiseDetails?: AreaOfExpertiseDetails[];
  culturalCompetencies?: string[] | null;
  culturalCompetencyCategories?:
    | { category?: string; description?: string }[]
    | null;
  npi?: string;
  licenseNumber?: string[];
};

export const FacilityQuality = ({
  providerName,
  providerType,
  selectedLocation,
  tabTitle,
  organizationCode,
  areaOfExpertise,
  areaOfExpertiseDetails,
  culturalCompetencies,
  culturalCompetencyCategories,
  npi,
  licenseNumber,
}: Props) => {
  const setShowAoeWidgetPage = useStore(StoreKeys.SET_SHOW_AOE_WIDGET_PAGE);
  const [qualityTabText, setQualityTabText] = useState<string>('');
  const patientSafetyText = useLagoon('facility-detail-quality-tab')();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const coverageType = useCoverageType();

  const facilityAttributes = useLagoon('facility-attributes')();

  const category = facilityAttributes?.find(
    (row) => row?.facilitySubCategoryCode === organizationCode?.[0]
  );

  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();

  const [isFacilityAOEDisplayEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.PROVIDER_FACILITY_DETAILS_AOE_DISPLAY,
  ]);

  const leapfrogDisclaimerContent = find(lagoonData, {
    key: ConstantsLagoon.LEAPFROG.LEAPFROG_DISCLAIMER_CONTENT,
  });

  const isPlatinumProviderIdentifierEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER
  );

  const platinumRecognitionPopoverContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.PLATINUM_RECOGNITION_POPOVER_CONTENT,
  });

  const isGEHAV2 = shouldUseGehaV2Styling();

  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const mobileScreen = useMediaQuery(mobileOnly) || isWidget;
  const phoneScreen = useMediaQuery(phoneOnly);
  const widgetDesktopScreen = !useMediaQuery(phoneOnly) && isWidget;
  const {
    facilityProviderSpecialties,
    isPlatinumProvider,
    leapFrogRatings,
    accreditations,
  } = selectedLocation;

  useEffect(() => {
    if (providerName) {
      if (isPlatinumProvider) {
        adobeLinkTrackEvent({
          name: Constants.ADOBE_TRACKING.PLATINUM_RECOGNITION,
          location: 'body:facility details',
          type: 'internal',
        });
      }
      adobePageTrackEvent();
    }
  }, [providerName]);

  useEffect(() => {
    if (patientSafetyText) {
      const FACILITY_QUALITY_TAB = patientSafetyText.find(
        (x) => x.key === 'FACILITY_QUALITY_TAB'
      );
      setQualityTabText(FACILITY_QUALITY_TAB?.patientSafetyRating);
    }
  }, [patientSafetyText]);
  const { t } = useTranslation();
  const notAvailableOrUnknownText = useNotAvailableUnknownText();

  // AOE Drawer state
  const [isAoeDrawerOpen, setIsAoeDrawerOpen] = useState(false);
  const [isCulturalCompetencyOpen, setIsCulturalCompetencyOpen] =
    useState(false);
  const isAoeDescriptionDrawerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AOE_DESCRIPTION_DRAWER
  );
  const aoeDescriptionButtonData = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.AOE_DESCRIPTION_BUTTON_TEXT,
  });
  const isAoeDrawerVisible =
    isAoeDescriptionDrawerEnabled &&
    areaOfExpertiseDetails &&
    areaOfExpertiseDetails.length > 0;
  const aoeLinkText =
    aoeDescriptionButtonData?.message ||
    t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.WHAT_IS_THIS') ||
    'What is this?';

  const culturalCompetencyCategoryList = Array.isArray(
    culturalCompetencyCategories
  )
    ? culturalCompetencyCategories.filter(
        (item) => item?.category || item?.description
      )
    : [];

  const culturalCompetenciesList = Array.isArray(culturalCompetencies)
    ? culturalCompetencies.filter(Boolean)
    : [];

  const normalizeCompetencyValue = (value: string) =>
    value.toLowerCase().replace(/[^a-z]/g, '');

  const isNotAvailableUnknownCompetency = (value?: string) => {
    if (!value) return false;

    const normalizedValue = normalizeCompetencyValue(value);
    const notAvailableUnknownValues = new Set([
      normalizeCompetencyValue(
        notAvailableOrUnknownText?.providerDetails || ''
      ),
      'notavailableunknown',
      'notavailableuknown',
      'notavailable',
      'unknown',
    ]);

    return notAvailableUnknownValues.has(normalizedValue);
  };

  const filterUnknownValues = (values: string[]) =>
    values.filter((value) => !isNotAvailableUnknownCompetency(value));

  const culturalCompetencyCategoriesList = culturalCompetencyCategoryList
    .map((item) => item?.category || item?.description)
    .filter(Boolean);

  const culturalCompetencyList =
    culturalCompetencyCategoriesList.length > 0
      ? filterUnknownValues(culturalCompetencyCategoriesList)
      : filterUnknownValues(culturalCompetenciesList);

  const isCulturalCompetencyVisible = culturalCompetencyList.length > 0;

  const openCulturalCompetencyModal = () => {
    setIsCulturalCompetencyOpen(true);
    adobeModalTrackEvent(Constants.ADOBE_TRACKING.CULTURAL_COMPETENCY_TRAINING);
  };

  const onCulturalCompetencyKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCulturalCompetencyModal();
    }
  };

  const culturalCompetencyContent = useMemo(() => {
    if (!isCulturalCompetencyVisible) return null;

    return (
      <TabContainer>
        <Flex
          alignItems="center"
          css={{ marginBottom: '12px' }}
          direction="row"
        >
          <Heading
            css={{
              ...headerText(mobileScreen),
            }}
            data-auto-testid="cultural-competency-heading"
            data-testid="cultural-competency-heading"
            level={2}
            size="sm"
          >
            {t(
              'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.CULTURAL_COMPETENCY_TRAINING'
            )}
          </Heading>
          <Flex
            aria-label={
              t(
                'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.CULTURAL_COMPETENCY_TRAINING'
              ) || 'Cultural competency training'
            }
            css={{
              display: 'flex',
              cursor: 'pointer',
              marginLeft: '5px',
            }}
            data-auto-testid="cultural-competency-tooltip-button"
            data-testid="cultural-competency-tooltip-button"
            onClick={openCulturalCompetencyModal}
            onKeyDown={onCulturalCompetencyKeyDown}
            role="button"
            tabIndex={0}
          >
            <IconSymbol
              css={{
                'abyss-icon-symbol': {
                  marginTop: '-2px',
                },
              }}
              icon="info"
              size={mobileScreen ? 18 : 24}
              variant="outlined"
            />
          </Flex>
        </Flex>
        {culturalCompetencyList.map((item, index) => (
          <Layout.Group
            data-testid={`cultural-competency-group-${index}`}
            key={`cultural-competency-group-${index}`}
          >
            <Text
              css={qualityText(mobileScreen, isGEHAV2)}
              data-auto-testid="cultural-competency-type"
              data-testid="cultural-competency-type"
            >
              {item}
            </Text>
          </Layout.Group>
        ))}
      </TabContainer>
    );
  }, [
    culturalCompetencyList,
    isCulturalCompetencyVisible,
    mobileScreen,
    isGEHAV2,
    t,
  ]);

  const areaOfExpertiseContent = useMemo(() => {
    if (!isFacilityAOEDisplayEnabled) return null;

    return (
      <TabContainer>
        <Heading
          css={{
            paddingBottom: '12px',
            ...headerText(mobileScreen),
          }}
          data-auto-testid="area-of-expertise-heading"
          data-testid="area-of-expertise-heading"
          level={2}
          size="sm"
        >
          {t('FACILITY_DETAILS.QUALITY_TAB.AREAS_OF_EXPERTISE')}
        </Heading>
        {areaOfExpertise && areaOfExpertise.length > 0 ? (
          <>
            {areaOfExpertise.map((item) => (
              <Layout.Group
                data-testid={`area-of-expertise-group-${item}`}
                key={`area-of-expertise-group-${item}`}
              >
                <Text
                  color="$core.color.neutral.90"
                  data-auto-testid="area-of-expertise-type"
                  data-testid="area-of-expertise-type"
                  fontSize="lg"
                  fontWeight="medium"
                >
                  {item}
                </Text>
              </Layout.Group>
            ))}
            {isAoeDrawerVisible && (
              <LinkContainer
                analyticsInfo={{ location: 'body:areas of expertise' }}
                before={
                  <IconSymbol icon="info" size="$sm" variant="outlined" />
                }
                css={{ marginTop: '16px' }}
                data-auto-testid="view-definition-aoe-facility"
                data-testid="view-definition-aoe-facility"
                onClick={() =>
                  isWidget
                    ? setShowAoeWidgetPage(true)
                    : setIsAoeDrawerOpen(true)
                }
                size="$md"
                text={aoeLinkText}
              />
            )}
          </>
        ) : (
          <Text
            css={qualityText(mobileScreen, isGEHAV2)}
            data-testid="area-of-expertise-heading-error"
          >
            {notAvailableOrUnknownText?.facilityDetailsAOE}
          </Text>
        )}
      </TabContainer>
    );
  }, [
    isFacilityAOEDisplayEnabled,
    areaOfExpertise,
    isAoeDrawerVisible,
    aoeLinkText,
    mobileScreen,
    isGEHAV2,
    t,
    notAvailableOrUnknownText?.facilityDetailsAOE,
  ]);

  const { hasJCAHOAccreditation } = facilityProviderSpecialties || {};
  const hasCARFAccreditation = accreditations && hasKeyCARF(accreditations);

  const { lineOfBusiness } = getCurrentMember();
  const isCns = isCnsMember(lineOfBusiness);

  const patientSafety = (
    <StackDiv
      css={stackDivStyle(mobileScreen, widgetDesktopScreen)}
      data-testid="facility-quality-tab"
    >
      <TabContainer key={Constants.PROVIDER_DETAILS.QUALITY}>
        <Heading
          css={{
            paddingBottom: '12px',
            ...headerText(mobileScreen),
          }}
          data-auto-testid="facility-quality-tab-patient-safety-ratings-heading"
          data-testid="facility-quality-tab-patient-safety-ratings-heading"
          level={2}
          size="sm"
        >
          {t('FACILITY_DETAILS.QUALITY_TAB.PATIENT_SAFETY')}
        </Heading>
        <Text
          css={qualityText(mobileScreen, isGEHAV2)}
          data-auto-testid="facility-quality-tab-patient-safety-ratings-text"
          data-testid="facility-quality-tab-patient-safety-ratings-text"
        >
          {t(qualityTabText)}
          <Link
            css={{
              'abyss-link-root': {
                display: 'inline-flex',
                alignItems: 'center',
              },
            }}
            data-auto-testid="my-benefits-get-a-ride"
            data-testid="my-benefits-get-a-ride"
            href="https://www.leapfroggroup.org/ratings-reports"
          >
            <Text
              color="$core.color.brand.60"
              fontSize={mobileScreen ? '14.22px' : '16px'}
            >
              {' '}
              {t('FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_GROUP')}
            </Text>
          </Link>
        </Text>
      </TabContainer>
    </StackDiv>
  );

  const leapFroPopoverContent = t(
    'FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_RATING_INFORMATION_CONTENT'
  );

  const leapFrogPopoverText = t(
    'FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_RATING_INFORMATION'
  );

  const leapFrogText = (
    <LeapFrogContainer>
      <Heading
        css={{
          '&.abyss-heading-root': {
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px !important',
            lineHeight: '20px !important',
            color: '$core.color.brand.60 !important',
            ...(phoneScreen && {
              fontSize: '12.64px !important',
            }),
          },
        }}
        data-auto-testid="leap-frog-disclaimer-heading"
        data-testid="leap-frog-disclaimer-heading"
        level={3}
        size="xs"
      >
        {t('FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_RATING_INFORMATION')}
        <Drawer
          css={landingDrawerIcon}
          header={t('FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_RATING_INFORMATION')}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          position="right"
          size="200"
        >
          {t(
            'FACILITY_DETAILS.QUALITY_TAB.LEAPFROG_RATING_INFORMATION_CONTENT'
          )}
        </Drawer>
        {phoneScreen ? (
          <IconSymbol
            css={{
              'abyss-icon-symbol': {
                marginLeft: '4px',
              },
            }}
            data-auto-testid="leap-frog-disclaimer-drawer"
            data-testid="leap-frog-disclaimer-drawer"
            icon="info"
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            size={16}
            variant="outlined"
          />
        ) : (
          <React.Fragment>
            <Popover
              content={leapFroPopoverContent}
              css={{
                'abyss-popover-trigger': {
                  display: 'block',
                },
                'abyss-popover-trigger-icon': {
                  marginLeft: '4px',
                },
              }}
              data-auto-testid="leap-frog"
              data-testid="leap-frog"
              position={mobileScreen ? 'top' : 'right'}
              title={leapFrogPopoverText}
              width={500}
            />
          </React.Fragment>
        )}
      </Heading>
      <Text
        css={leapFrogTextStyle(phoneScreen)}
        data-auto-testid="leap-frog-disclaimer-text"
        data-testid="leap-frog-disclaimer-text"
      >
        {leapfrogDisclaimerContent?.message ?? ''}
        <Link
          css={{
            'abyss-link-root': {
              display: 'inline',
            },
          }}
          data-auto-testid="leap-frog-hospital-safety"
          data-testid="leap-frog-hospital-safety"
          href="http://www.hospitalsafetyscore.org"
        >
          <Text
            color="$core.color.brand.60"
            fontSize={mobileScreen ? '14.22px' : '16px'}
          >
            {' '}
            http://www.hospitalsafetyscore.org.
          </Text>
        </Link>
      </Text>
    </LeapFrogContainer>
  );

  const ratings = <LeapFrogRating leapFrogRatings={leapFrogRatings} />;

  const leapFrogRatingsBlock = (
    <React.Fragment>
      {patientSafety}
      {mobileScreen ? (
        <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen)}>
          {ratings}
          {leapFrogText}
        </StackDiv>
      ) : (
        <React.Fragment>
          <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen)}>
            {ratings}
          </StackDiv>
          <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen)}>
            {leapFrogText}
          </StackDiv>
        </React.Fragment>
      )}
    </React.Fragment>
  );

  const getIndicatorsForImpressions = (selectedLocation) => {
    const indicators: string[] = [];

    const featureFlags = useLagoon('feature-flags')();
    const isPlatinumProviderIdentifierEnabled = getFeatureFlag(
      featureFlags,
      ConstantsLagoon.FEATURE_FLAGS
        .ENABLE_PLATINUM_PROVIDER_IDENTIFIER_ANALYTICS
    );

    if (selectedLocation?.acceptingNewPatients)
      indicators.push(Constants.ADOBE_TRACKING.NEW_PATIENTS);

    if (selectedLocation?.hasPCPServiceAvailability)
      indicators.push(Constants.ADOBE_TRACKING.PCP_SERVICES);

    if (
      selectedLocation?.isPlatinumProvider &&
      isPlatinumProviderIdentifierEnabled
    ) {
      indicators.push(Constants.ADOBE_TRACKING.PLATINUM_RECOGNITION);
    }

    if (selectedLocation?.hasEveningAppointments)
      indicators.push(Constants.ADOBE_TRACKING.EVENING_APPOINTMENT);

    if (selectedLocation?.ddpCode)
      indicators.push(
        `ddp ${selectedLocation.defaultTranslatedValues.ddpCode}`
      );
    return indicators?.join('|').toLocaleLowerCase();
  };

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerType
    )} details`,
    impressionBlock: {
      type: 'facilities',
      indicator: getIndicatorsForImpressions(selectedLocation),
    },
  });

  return (
    <React.Fragment>
      {!isCns &&
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isPlatinumProvider &&
      isPlatinumProviderIdentifierEnabled ? (
        <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen)}>
          <Popover
            content={platinumRecognitionPopoverContent?.message ?? ''}
            css={{
              display: 'flex',
              gap: '4px',
            }}
            position="top"
            title={platinumRecognitionPopoverContent?.title ?? ''}
            width="500px"
          >
            <TabContainer
              data-auto-testid="platinum-recognition-badge-container"
              data-testid="platinum-recognition-badge-container"
            >
              <Badge css={PlatinumBadgeStyle} variant="neutral">
                {t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
              </Badge>
            </TabContainer>
            <IconSymbol
              css={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
              }}
              data-auto-testid="platinum-provider-Info"
              data-testid="platinum-provider-Info"
              icon="info"
              onClick={() => {
                adobeModalTrackEvent(
                  Constants.ADOBE_TRACKING.PLATINUM_RECOGNITION
                );
                setIsOpen(true);
              }}
              size={16}
              variant="outlined"
            />
          </Popover>
        </StackDiv>
      ) : null}
      {category?.facilityCategory?.toLowerCase() ===
        FacilityCategory.HOSPITAL.toLowerCase() && leapFrogRatings?.length > 0
        ? leapFrogRatingsBlock
        : null}
      <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen, true)}>
        <TabContainer>
          <Heading
            css={{
              paddingBottom: '12px',
              ...headerText(mobileScreen),
            }}
            data-auto-testid="accreditation-heading"
            data-testid="accreditation-heading"
            level={2}
            size="sm"
          >
            {t('PROVIDER_DETAILS.QUALITY_TAB.ACCREDITATION_TITLE')}
          </Heading>
          {hasJCAHOAccreditation && (
            <Text
              css={qualityText(mobileScreen, isGEHAV2)}
              data-testid="facility-accreditation-heading-jacaho"
            >
              {t(Constants.FACILITY_DETAILS.QUALITY_TAB.ACCREDITATION[0])}
            </Text>
          )}
          {hasCARFAccreditation && (
            <Text
              css={qualityText(mobileScreen, isGEHAV2)}
              data-testid="facility-accreditation-heading-carf"
            >
              {t(Constants.FACILITY_DETAILS.QUALITY_TAB.ACCREDITATION[1])}
            </Text>
          )}
          {!hasJCAHOAccreditation && !hasCARFAccreditation && (
            <Text
              css={qualityText(mobileScreen, isGEHAV2)}
              data-testid="facility-accreditation-heading-error"
            >
              {notAvailableOrUnknownText?.facilityDetails}
            </Text>
          )}
        </TabContainer>
      </StackDiv>
      <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen, true)}>
        {areaOfExpertiseContent}
      </StackDiv>
      {isCulturalCompetencyVisible && (
        <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen, true)}>
          {culturalCompetencyContent}
        </StackDiv>
      )}
      <StackDiv css={stackDivStyle(mobileScreen, widgetDesktopScreen, true)}>
        <TabContainer>
          <Heading
            css={{
              paddingBottom: '12px',
              ...headerText(mobileScreen),
            }}
            data-auto-testid="facility-heading"
            data-testid="facility-heading"
            level={2}
            size="sm"
          >
            {t('FACILITY_DETAILS.QUALITY_TAB.FACILITY_DETAILS_TITLE')}
          </Heading>

          <Layout.Stack alignItems="left" space={12}>
            <ListOtherProviderDetails
              copyIconSize={20}
              customCss={{ fontSize: '16px' }}
              idName={t(
                'FACILITY_DETAILS.QUALITY_TAB.NATIONAL_PROVIDER_IDENTIFIER'
              )}
              idNumber={npi}
              notAvailableCss={{ color: '#323333', fontWeight: 'normal' }}
            />
            <ListOtherProviderDetails
              copyIconSize={20}
              customCss={{ fontSize: '16px' }}
              idName={t('FACILITY_DETAILS.QUALITY_TAB.LICENSE_NUMBER')}
              idNumber={licenseNumber?.join(', ')}
              notAvailableCss={{ color: '#323333', fontWeight: 'normal' }}
            />
          </Layout.Stack>
        </TabContainer>
      </StackDiv>

      {/* AOE Drawer Component */}
      {isAoeDrawerVisible && areaOfExpertiseDetails && (
        <AoeDrawer
          areaOfExpertiseDetails={areaOfExpertiseDetails}
          isOpen={isAoeDrawerOpen}
          setIsOpen={setIsAoeDrawerOpen}
        />
      )}
      <CulturalCompetencyTraining
        isOpen={isCulturalCompetencyOpen}
        setIsOpen={setIsCulturalCompetencyOpen}
      />
    </React.Fragment>
  );
};
